/**
 * Business Partner Deals View
 *
 * Displays deals filtered by a specific business partner (service provider).
 */

import { useEffect, useCallback, useMemo } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, ArrowLeft } from "lucide-react";
import { Card, Loader, Button, Pagination } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { DealsTable } from "@/ui/organisms";
import { routerPaths } from "@/ui/router";
import type { TAsyncState, TDeal, TPaginatedDeals } from "@/store";
import { useServerTable } from "@/useCases/useServerTable";
import {
    textParam,
    selectParam,
    booleanParam,
    dateRangeParam,
    sortParam,
} from "@/useCases/serverTableQuery";
import type { TServerTableQueryInput } from "@/useCases/useServerTable";

/**
 * Service Provider basic info
 */
export type TBusinessPartnerBasicInfo = {
    id: string;
    name: string;
    code: string;
};

type TBpFilterOptions = {
    serviceProviders: Array<{ id: string; name: string }>;
    portfolioCompanies: Array<{ id: string; name: string }>;
};

type TBpDealsQuery = {
    skip?: number;
    limit?: number;
    service_provider_id?: string;
    portfolio_company_id?: string;
    business_partner_id?: string;
    name?: string;
    deal_file_type?: string;
    is_active?: boolean;
    created_from?: string;
    created_to?: string;
    sort_by?: string;
    sort_dir?: string;
};

export type TViewBusinessPartnerDealsParams = {
    useCases: () => {
        onMount: (bpId: string) => void;
        onUnmount: () => void;
        useDealsState: () => TAsyncState<TPaginatedDeals>;
        useBusinessPartner: () => {
            data: TBusinessPartnerBasicInfo | null;
            loading: boolean;
            error: string | null;
        };
        usePortfolioCompanies: () => {
            data: Array<{ id: string; name: string; code: string }> | null;
            loading: boolean;
        };
        onDealClick: (deal: TDeal, bpId: string) => void;
        onCreateDealClick: (bpId: string) => void;
        onPageChange: (page: number) => void;
        onRefresh: (bpId: string) => void;
        onToggleActive: (deal: TDeal, bpId: string) => void | Promise<void>;
        canCreateDeal: boolean;
        loadDeals: (query: TBpDealsQuery, signal?: AbortSignal) => void;
        useFilterOptions: () => TBpFilterOptions;
        canViewDealDetail: boolean;
        canToggleActive: boolean;
    };
};

export type TViewBusinessPartnerDealsProps = Record<string, never>;

export const createViewBusinessPartnerDeals = ({ useCases }: TViewBusinessPartnerDealsParams) => {
    return function ViewBusinessPartnerDeals() {
        const { bpId } = useParams<{ bpId: string }>();
        const navigate = useNavigate();
        const {
            onMount,
            onUnmount,
            useDealsState,
            useBusinessPartner,
            usePortfolioCompanies,
            onDealClick,
            onCreateDealClick,
            onPageChange: _onPageChange,
            onRefresh,
            onToggleActive,
            canCreateDeal,
            loadDeals,
            useFilterOptions,
            canViewDealDetail,
            canToggleActive,
        } = useCases();

        const dealsState = useDealsState();
        const businessPartner = useBusinessPartner();
        usePortfolioCompanies(); // kept for compatibility; facets sourced from filterOptions
        const filterOptions = useFilterOptions();

        const PAGE_SIZE = 50;

        const dealsToQuery = useCallback((input: TServerTableQueryInput): TBpDealsQuery => {
            const created = dateRangeParam(input.columnFilters, "createdAt");
            return {
                skip: (input.page - 1) * PAGE_SIZE,
                limit: PAGE_SIZE,
                // The structural BP filter (service_provider_id / portfolio_company_id)
                // is merged in by the wiring and overrides the matching key, so this
                // column filter only narrows the *other* dimension (e.g. picking a
                // portfolio company while viewing a service provider's deals).
                business_partner_id: selectParam(input.columnFilters, "businessPartnerName"),
                portfolio_company_id: selectParam(input.columnFilters, "portfolioCompanyId"),
                name: textParam(input.columnFilters, "name"),
                deal_file_type: selectParam(input.columnFilters, "dealFileType"),
                is_active: booleanParam(input.columnFilters, "isActive"),
                created_from: created?.from,
                created_to: created?.to,
                ...sortParam(input.sorting),
            };
        }, []);

        const server = useServerTable({
            pageSize: PAGE_SIZE,
            toQuery: dealsToQuery,
            fetchData: loadDeals,
        });
        const { refetch } = server;

        const spOptions = useMemo(
            () => filterOptions.serviceProviders.map((sp) => ({ id: sp.id, name: sp.name })),
            [filterOptions.serviceProviders],
        );
        const pcOptions = useMemo(
            () => filterOptions.portfolioCompanies.map((pc) => ({ id: pc.id, name: pc.name })),
            [filterOptions.portfolioCompanies],
        );

        useEffect(() => {
            if (bpId) {
                onMount(bpId);
            }
            return onUnmount;
        }, [bpId, onMount, onUnmount]);

        // Trigger a refetch once the business partner has resolved so that
        // loadDeals runs with lastBpId and isPortfolioCompanyRef already set.
        // By this point the wire has set both synchronously (before this render),
        // so the structural filter (service_provider_id / portfolio_company_id)
        // is correct and there is no stale-ref window.
        // No infinite loop: refetch bumps refetchNonce → fetch → updates
        // dealsListState, NOT businessPartner.data.id, so this effect does not
        // re-fire after the initial load.
        useEffect(() => {
            if (businessPartner.data?.id) {
                refetch();
            }
        }, [businessPartner.data?.id, refetch]);

        const handleCreateDeal = useCallback(() => {
            if (bpId) {
                onCreateDealClick(bpId);
            }
        }, [bpId, onCreateDealClick]);

        const handleDealClick = useCallback(
            (deal: TDeal) => {
                if (bpId) {
                    onDealClick(deal, bpId);
                }
            },
            [bpId, onDealClick],
        );

        const handleRefresh = useCallback(() => {
            if (bpId) {
                onRefresh(bpId);
            }
        }, [bpId, onRefresh]);

        const handleToggleActive = useCallback(
            async (deal: TDeal) => {
                if (!bpId) {
                    return;
                }
                // Rejections propagate to the DealStatusCell confirmation modal.
                await onToggleActive(deal, bpId);
            },
            [bpId, onToggleActive],
        );

        // Loading state — treat idle as loading: the BP-resolved refetch is imminent
        if (
            businessPartner.loading ||
            dealsState.status === "pending" ||
            dealsState.status === "idle"
        ) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        // Error state
        if (businessPartner.error) {
            return (
                <div className="text-center py-8">
                    <p className="text-destructive">{businessPartner.error}</p>
                    <Button
                        variant="outline"
                        onClick={() => navigate(routerPaths.admin.businessPartners)}
                        className="mt-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Business Partners
                    </Button>
                </div>
            );
        }

        if (dealsState.status === "rejected") {
            return (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-semibold">
                                Deals: {businessPartner.data?.name}
                            </h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                Business Partners &gt; {businessPartner.data?.name} &gt; Deals
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => navigate(routerPaths.admin.businessPartners)}
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </div>
                    <EmptyState
                        variant="error"
                        title="Failed to load deals"
                        description={dealsState.error?.message || "An error occurred"}
                        action={{ label: "Try Again", onClick: handleRefresh }}
                    />
                </div>
            );
        }

        const deals = dealsState.data?.items ?? [];

        return (
            <div className="space-y-6">
                {/* Header with breadcrumb */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold">
                            Deals: {businessPartner.data?.name}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            <button
                                onClick={() => navigate(routerPaths.admin.businessPartners)}
                                className="hover:text-foreground hover:underline"
                            >
                                Business Partners
                            </button>
                            {" > "}
                            <span>{businessPartner.data?.name}</span>
                            {" > "}
                            <span>Deals</span>
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate(routerPaths.admin.businessPartners)}
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                        {canCreateDeal && (
                            <Button onClick={handleCreateDeal}>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Deal
                            </Button>
                        )}
                    </div>
                </div>

                {/* Deals table */}
                <Card className="overflow-hidden">
                    {deals.length === 0 &&
                    server.columnFilters.length === 0 &&
                    dealsState.status === "fulfilled" ? (
                        <EmptyState
                            title="No deals yet"
                            description={`Create your first deal for ${businessPartner.data?.name}.`}
                            action={
                                canCreateDeal
                                    ? {
                                          label: "Create Deal",
                                          onClick: handleCreateDeal,
                                      }
                                    : undefined
                            }
                        />
                    ) : (
                        <DealsTable
                            deals={deals}
                            loading={false}
                            onDealClick={canViewDealDetail ? handleDealClick : undefined}
                            canToggleActive={canToggleActive}
                            onToggleActive={handleToggleActive}
                            storageKey="business-partner-deals"
                            serviceProviders={spOptions}
                            portfolioCompanies={pcOptions}
                            columnFilters={server.columnFilters}
                            onColumnFiltersChange={server.onColumnFiltersChange}
                            sortingState={server.sorting}
                            onSortingChange={server.onSortingChange}
                            rowCount={buildRowCount({
                                total: dealsState.data?.total ?? 0,
                                totalUnfiltered: dealsState.data?.totalUnfiltered,
                                hasActiveFilters: server.columnFilters.length > 0,
                            })}
                        />
                    )}
                    <Pagination
                        page={server.page}
                        pageSize={PAGE_SIZE}
                        total={dealsState.data?.total ?? 0}
                        onPageChange={server.setPage}
                        className="px-6 py-4 border-t"
                    />
                </Card>
            </div>
        );
    };
};
